import React from 'react';
import { Checkbox, FormControlLabel, Grid, InputLabel, OutlinedInput } from '@mui/material';
import DefaultSelectClient from '../../../../components/default-select-client';
import DefaultUploadFile from '../../../../components/default-upload-file';
import { DateField, FieldError, FormSectionTitle } from '../../../../components/form-page-parts';
import { ValidationErrors } from '../../../../utils/validation';
import { LookupSelect, toLookupOptions } from '../../shared/lookup-select';
import { useIrpCreateEditState } from '../context/irp-create-edit-context';
import { IrpField } from '../context/irp-validation';
import { IIrpAddEditRequest } from '../../../../model/scp/irp/request/irp-add-edit-request';

interface IrpGeneralFormProps {
    errors: ValidationErrors<IrpField>;
    onChange: (e: any) => void;
    onDateChange: (field: keyof IIrpAddEditRequest, value: string | null) => void;
    onFileChange: (file: File) => void;
}

// Aba "Dados da IRP": identificação, prazos, descrição e (na inclusão) o documento inicial
export function IrpGeneralForm({ errors, onChange, onDateChange, onFileChange }: IrpGeneralFormProps) {
    const { irpRequest, irpId, file, modality, disputeMode, instrumentSummons, legalSupport, typeDocument, unidEntity } = useIrpCreateEditState();
    const isEdit = irpId !== undefined;
    const unitOptions = unidEntity.map((unit) => ({ value: unit.unitCode, label: `${unit.unitCode} - ${unit.name}` }));

    return (
        <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
                <FormSectionTitle title="Cliente" first />
                <DefaultSelectClient required handleOnChange={onChange} clientId={irpRequest.clientId} error={errors.clientId || false} />
            </Grid>

            <Grid item xs={12}>
                <FormSectionTitle title="Identificação da Intenção de Registro de Preço" />
            </Grid>
            <Grid item xs={12} sm={6}>
                <LookupSelect required label="Instrumento Convocatório" name="select-type-calling-instrument" value={irpRequest.typeCallingInstrumentId} options={toLookupOptions(instrumentSummons)} onChange={onChange} error={errors.typeCallingInstrumentId} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <LookupSelect required label="Modalidade" name="select-modality" value={irpRequest.modalityId} options={toLookupOptions(modality)} onChange={onChange} error={errors.modalityId} />
            </Grid>
            <Grid item xs={12} sm={4}>
                <LookupSelect required label="Modo de Disputa" name="select-dispute-mode" value={irpRequest.disputeModeId} options={toLookupOptions(disputeMode)} onChange={onChange} error={errors.disputeModeId} />
            </Grid>
            <Grid item xs={12} sm={4}>
                <LookupSelect required label="Amparo Legal" name="select-legal-support" value={irpRequest.legalSupportId} options={toLookupOptions(legalSupport, false)} onChange={onChange} error={errors.legalSupportId} />
            </Grid>
            <Grid item xs={12} sm={4}>
                <LookupSelect required label="Unidade Gerenciadora" name="select-managing-unit" value={irpRequest.codManagingUnit} options={unitOptions} onChange={onChange} error={errors.codManagingUnit} />
            </Grid>
            <Grid item xs={12} sm={4} lg={2}>
                <InputLabel required>Exercício</InputLabel>
                <OutlinedInput name="input-year-irp" type="number" fullWidth autoComplete="off" value={irpRequest.yearIrp || ''} onChange={onChange} error={!!errors.yearIrp} />
                <FieldError message={errors.yearIrp} />
            </Grid>
            <Grid item xs={12} sm={4} lg={2}>
                <InputLabel required>Número</InputLabel>
                <OutlinedInput name="input-number-irp" fullWidth autoComplete="off" value={irpRequest.numberIrp || ''} onChange={onChange} error={!!errors.numberIrp} />
                <FieldError message={errors.numberIrp} />
            </Grid>
            <Grid item xs={12} sm={4} lg={2}>
                <InputLabel>Número Processo</InputLabel>
                <OutlinedInput name="input-process-number" fullWidth autoComplete="off" value={irpRequest.processNumber || ''} onChange={onChange} />
            </Grid>

            <Grid item xs={12}>
                <FormSectionTitle title="Adesão e Prazos" />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
                <DateField
                    required
                    label="Data Final para Manifestação de Interesse"
                    value={irpRequest.finalInterestManifestationDate}
                    onChange={(value) => onDateChange('finalInterestManifestationDate', value)}
                    error={errors.finalInterestManifestationDate}
                />
            </Grid>
            <Grid item xs={12} sm={6} lg={8} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                    control={<Checkbox name="check-ind-possibility-adhesion" onChange={onChange} checked={irpRequest.indPossibilityAdhesion || false} />}
                    label="Possibilidade de Adesão"
                />
            </Grid>

            <Grid item xs={12}>
                <FormSectionTitle title="Descrição" />
            </Grid>
            {!isEdit && (
                <Grid item xs={12}>
                    <InputLabel required>Título</InputLabel>
                    <OutlinedInput name="input-title" fullWidth autoComplete="off" value={irpRequest.title || ''} onChange={onChange} error={!!errors.title} />
                    <FieldError message={errors.title} />
                </Grid>
            )}
            <Grid item xs={12}>
                <InputLabel required>Objeto</InputLabel>
                <OutlinedInput name="input-object" fullWidth autoComplete="off" multiline rows={4} value={irpRequest.objectIrp || ''} onChange={onChange} error={!!errors.objectIrp} />
                <FieldError message={errors.objectIrp} />
            </Grid>
            <Grid item xs={12}>
                <InputLabel>Informações Complementares</InputLabel>
                <OutlinedInput name="input-complementary-information" fullWidth autoComplete="off" multiline rows={3} value={irpRequest.complementaryInformation || ''} onChange={onChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <InputLabel required>URL do Sistema de Origem</InputLabel>
                <OutlinedInput name="input-url-system-origin" fullWidth autoComplete="off" value={irpRequest.urlSystemOrigin || ''} onChange={onChange} error={!!errors.urlSystemOrigin} />
                <FieldError message={errors.urlSystemOrigin} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <InputLabel>URL do Processo Eletrônico</InputLabel>
                <OutlinedInput name="input-url-electronic-process" fullWidth autoComplete="off" value={irpRequest.urlElectronicProcess || ''} onChange={onChange} />
            </Grid>

            {isEdit ? (
                <Grid item xs={12}>
                    <FormSectionTitle title="Retificação" />
                    <InputLabel>Justificativa da Retificação</InputLabel>
                    <OutlinedInput name="input-justification" fullWidth autoComplete="off" multiline rows={2} value={irpRequest.justification || ''} onChange={onChange} />
                </Grid>
            ) : (
                <React.Fragment>
                    <Grid item xs={12}>
                        <FormSectionTitle title="Documento" />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={4}>
                        <LookupSelect required label="Tipo de Documento" name="select-type-document" value={irpRequest.documentTypeId} options={toLookupOptions(typeDocument)} onChange={onChange} error={errors.documentTypeId} />
                    </Grid>
                    <Grid item xs={12} lg={8}>
                        <InputLabel required>Arquivo</InputLabel>
                        <DefaultUploadFile props={{ id: 'file-upload-irp', setFileUpload: onFileChange, file, extensions: ['pdf'], maxFileSizeMb: 30, error: !!errors.file }} />
                        <FieldError message={errors.file} />
                    </Grid>
                </React.Fragment>
            )}
        </Grid>
    );
}
